// /calendar/events/:id — the event detail page (W6 follow-up).
//
// Why a page and not more sheet: DESIGN §11.1's cross-module pattern is
//   list tap -> BottomSheet -> sheet's primary action -> navigate to detail
// and every other content module ends in a real route (`/diaries/:id`,
// `/anniversaries/:id`, `/wishes/:id`, `/albums/:albumId`). The calendar had
// no such route, so the day sheet's "查看" on an owned event re-opened the
// sheet it was already in — a control that renders, takes clicks, and changes
// nothing, which is the §3.3 defect class by name.
//
// A projection item has no id (Q7) and keeps the "前往來源" deep link into the
// module that owns it. That asymmetry is the point of Q21: owned events get a
// real detail page, borrowed ones get sent home.
//
// Reads and writes ask different questions and are shown differently: a 404
// here means "not available to you" (the backend masks private events), so the
// page says so plainly and offers the way back — it must not render a retry
// button for something that will never succeed.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Pencil, Trash2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { ErrorState } from "@/components/primitives/ErrorState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { PageContainer, SectionStack } from "@/components/layout";
import { EventLinkPickerSheet } from "@/components/calendar/EventLinkPickerSheet";
import {
  LINK_SOURCE_LABEL,
  LINK_SOURCE_ROUTE,
  isLinkSourceType,
  type LinkSourceType,
} from "@/lib/i18n/calendar";
import {
  CATEGORY_ICON,
  CATEGORY_LABEL,
  VISIBILITY_LABEL,
  chipClasses,
} from "@/components/calendar/calendarIcons";
import {
  deleteEvent,
  getEvent,
  linkEvent,
  restoreEvent,
  unlinkEvent,
  type CalendarItem,
} from "@/lib/api/calendar";
import { formatRange } from "@/components/calendar/eventViewModel";
import { ApiError } from "@/lib/api/pair";
import { UndoBar } from "@/components/patterns/UndoBar";
import { HoldToConfirmButton } from "@/components/system/HoldToConfirmButton";
import { useUndoBar } from "@/hooks/useUndoBar";
import { useToast } from "@/components/ui/toast";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { queryKeys } from "@/lib/queries/keys";

const OWNER_ZH: Record<string, string> = { me: "你", partner: "TA" };

// `formatRange` and `toEditorItem` live in
// `components/calendar/eventViewModel`. They were lifted out to pay the
// route-size ratchet (AGENTS.md §3 #10): this file is on the known-debt list,
// and a baseline entry is a record of debt rather than permission to add to it.
//
// W40.1 F7 moved `toEditorItem`'s consumer: the editor is now
// `/calendar/events/:id/edit`, which is its second caller. One definition of
// 「whose event is this」 for two pages, so they cannot disagree.

export function CalendarEventDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["calendar-event", id],
    queryFn: () => getEvent(id),
    enabled: Boolean(id),
    retry: false,
  });

  // 404 is terminal, not transient: the backend masks a private event the
  // caller may not see. Rendering ErrorState with a retry here would offer a
  // button that can never succeed.
  const isMasked = query.error instanceof ApiError && query.error.status === 404;

  const me = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => {
      const r = await fetch("/api/v1/auth/me", { credentials: "include" });
      if (!r.ok) return null;
      return (await r.json()) as { user?: { id?: string } };
    },
    staleTime: 5 * 60_000,
  });
  const myId = me.data?.user?.id;

  const event = query.data;

  // W39.1 R1 (ADR-0080): same shape as the diary detail page — the delete no
  // longer confirms and no longer navigates, and the undo bar renders in place
  // so the user's eyes stay on the button they just pressed.
  const undo = useUndoBar<{ id: string }>({
    onExpire: () => nav("/calendar"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteEvent(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      undo.begin({ id });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => restoreEvent(id),
    onSuccess: async () => {
      undo.clear();
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (err) => {
      // Keep the bar on screen: the delete really happened, and hiding it would
      // leave no route back. But keeping it and saying *nothing* is the silence
      // W39.53 found all over the album surface — `console.error` is for the
      // log, not the person. Found by `__tests__/no-console-only-controls.guard.test.ts`.
      console.error("[calendar] restore failed", err);
      pushToast({
        variant: "danger",
        message: err instanceof ApiError ? `還原失敗：${err.message}` : "還原失敗，請稍後再試。",
      });
    },
  });

  /**
   * Invalidate every view that can hold a copy of this event.
   *
   * The form moved to its own route in W40.1 F7, so it no longer invalidates on
   * its way out — it PATCHes and navigates back here, and the refetch this
   * function does is what makes the detail page show the new title immediately
   * instead of the cached one.
   */
  async function refreshEvent(id: string) {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["calendar-event", id] }),
      qc.invalidateQueries({ queryKey: ["calendar-month"] }),
      qc.invalidateQueries({ queryKey: ["calendar-day"] }),
      qc.invalidateQueries({ queryKey: ["calendar-cycle"] }),
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
    ]);
  }

  /**
   * Q21: a source record is ONLY ever linked by hand. The endpoint shipped with
   * the module but nothing could reach it, so the whole manual-link path was
   * documented and unreachable — a shipped feature with no door. This is the
   * door.
   *
   * W53.1 D-1: `type` used to be the literal `"album"`. The backend has accepted
   * all four sources since W2 (`calendar.SOURCE_TYPES`), so 「相關」 was a dead
   * end for three quarters of what the API could already do — and the picker
   * above now asks the user which one they mean.
   */
  async function handleLink(type: LinkSourceType, sourceId: string) {
    if (!event) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      await linkEvent(event.id, type, sourceId);
      await refreshEvent(event.id);
      setLinkPickerOpen(false);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : "關聯失敗，請稍候再試");
    } finally {
      setLinkBusy(false);
    }
  }

  /** Un-linking is a real need: albums get reorganised, links outlive them. */
  async function handleUnlink() {
    if (!event) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      await unlinkEvent(event.id);
      await refreshEvent(event.id);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : "取消關聯失敗，請稍候再試");
    } finally {
      setLinkBusy(false);
    }
  }

  function handleBack() {
    // Return to the day the event lives on, so the user lands on context
    // rather than on whatever month they last browsed.
    if (event) {
      const d = new Date(`${event.start_date}T00:00:00`);
      nav(`/calendar?y=${d.getFullYear()}&m=${d.getMonth() + 1}`);
      return;
    }
    nav("/calendar");
  }

  return (
    <div data-testid="calendar-event-detail-page" className="flex flex-col pb-32">
      <PageHero
        // W45.0 `ADR-0115`: the eyebrow back link becomes the breadcrumb's parent
        // segment. The `onSelect` override is load-bearing — this page's "up" is
        // the *month the event lives in*, not the plain `/calendar` href, so the
        // behaviour travels with the affordance instead of living in a second
        // competing control.
        crumbs={[
          {
            to: "/calendar",
            label: "日曆",
            testId: "calendar-event-back",
            onSelect: (e) => {
              e.preventDefault();
              handleBack();
            },
          },
          { label: "詳情" },
        ]}
        title={event?.title ?? "事件"}
        subtitle={event ? formatRange(event) : undefined}
        trailing={
          event ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => nav(`/calendar/events/${id}/edit`)}
                className="tap-target inline-flex w-11 h-11 items-center justify-center text-ink-2 hover:text-ink-1 press"
                aria-label="編輯事件"
                data-testid="calendar-event-detail-edit"
              >
                <Pencil className="w-5 h-5" />
              </button>
              <HoldToConfirmButton
                onConfirm={() => deleteMutation.mutate()}
                label="刪除事件"
                icon={<Trash2 className="w-5 h-5" />}
                busy={deleteMutation.isPending}
                className="w-11 h-11"
                testId="calendar-event-detail-delete"
              />
            </div>
          ) : null
        }
      />

      <PageContainer vertical="loose">
        {query.isLoading ? (
          <LoadingState shape="card" count={2} />
        ) : isMasked ? (
          // W53.2 D-5 — all three of this page's blocks were outside the card
          // system: two bare `bg-paper-deep` and one with no fill at all, none
          // with a shadow. `bg-paper-deep` is `248 244 235` against the page's
          // colour wash — 1.1447:1 by DESIGN.md's own arithmetic, i.e. the card
          // was only findable because of the hairline, and on the colour-washed
          // ground this screen actually sits on, not even that.
          <Card data-testid="calendar-event-missing" className="p-s-4">
            <p className="text-body-sm font-semibold text-ink-1">找不到這個事件</p>
            <p className="mt-s-1 text-body-sm text-ink-2">
              它可能已被刪除，或屬於「僅我可見」而你看不到。
            </p>
          </Card>
        ) : query.isError ? (
          <ErrorState
            title="無法載入事件"
            description="請稍候再試一次"
            onRetry={() => void query.refetch()}
          />
        ) : event ? (
          <SectionStack density="loose">
            <Card
              className="p-s-5"
              data-testid="calendar-event-summary"
            >
              <div className="flex items-center gap-s-3">
                <span
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-md",
                    chipClasses(event.owner_id === myId ? "me" : "partner"),
                  )}
                  aria-hidden="true"
                >
                  {(() => {
                    const Icon =
                      CATEGORY_ICON[event.category as CalendarItem["category"]];
                    return Icon ? <Icon size={20} strokeWidth={1.5} /> : null;
                  })()}
                </span>
                <div className="min-w-0">
                  <p className="text-h3 text-ink-1">
                    {CATEGORY_LABEL[event.category as CalendarItem["category"]] ??
                      event.category}
                  </p>
                  <p className="mt-s-1 text-body-sm text-ink-2">
                    {OWNER_ZH[event.owner_id === myId ? "me" : "partner"]}的事 ·{" "}
                    {event.all_day ? "全日" : "有時刻"}
                  </p>
                </div>
                <span className="ml-auto shrink-0 rounded-pill bg-paper-calm px-s-3 py-s-1 text-meta text-ink-2">
                  {VISIBILITY_LABEL[event.visibility as "shared" | "private"] ??
                    event.visibility}
                </span>
              </div>
            </Card>

            {/* Had `rounded-lg border border-hairline` and **no background at
                all** — so on this screen the summary above it was one colour,
                this block was the page ground, and the two read as different
                levels of the page rather than as two cards.

                The `<dl>` stays a `<dl>`: it is a definition list and assistive
                tech reads it as one, which a `<Card>`'s `div` would flatten.
                `<Card>` wraps it and `p-0` keeps the definition rows flush to
                the card edge, exactly as they were against the bare border. */}
            <Card className="p-0">
              <dl className="flex flex-col divide-y divide-hairline">
              <div className="flex items-center justify-between px-s-4 py-s-3">
                <dt className="text-body-sm text-ink-3">日期</dt>
                <dd className="text-body-sm tabular-nums text-ink-1">
                  {formatRange(event)}
                </dd>
              </div>
              <div className="flex items-center justify-between px-s-4 py-s-3">
                <dt className="text-body-sm text-ink-3">誰的</dt>
                <dd className="text-body-sm text-ink-1">
                  {OWNER_ZH[event.owner_id === myId ? "me" : "partner"]}
                </dd>
              </div>
              <div className="flex items-center justify-between px-s-4 py-s-3">
                <dt className="text-body-sm text-ink-3">可見性</dt>
                <dd className="text-body-sm text-ink-1">
                  {VISIBILITY_LABEL[event.visibility as "shared" | "private"] ??
                    event.visibility}
                </dd>
              </div>
              {event.note ? (
                <div className="flex flex-col gap-s-1 px-s-4 py-s-3">
                  <dt className="text-body-sm text-ink-3">備註</dt>
                  <dd className="whitespace-pre-wrap text-body text-ink-1">
                    {event.note}
                  </dd>
                </div>
              ) : null}
              </dl>
            </Card>

            {event.source_type && event.source_id && isLinkSourceType(event.source_type) ? (
              <div className="flex flex-col gap-s-2">
                <Link
                  to={`${LINK_SOURCE_ROUTE[event.source_type]}${event.source_id}`}
                  className="flex items-center gap-s-2 rounded-md border border-hairline px-s-4 py-s-3 text-body-sm text-you-strong hover:bg-black/5"
                  data-testid="calendar-event-source-link"
                >
                  <Link2 className="w-4 h-4" aria-hidden />
                  查看關聯的
                  {/* W53.1 D-1: this read `CATEGORY_LABEL`, which has no `album`
                      key — albums are a link source but never a calendar
                      category — so a linked album rendered as the raw string
                      「查看關聯的album記錄」. */}
                  {LINK_SOURCE_LABEL[event.source_type]}
                  記錄
                </Link>
                <button
                  type="button"
                  onClick={handleUnlink}
                  disabled={linkBusy}
                  className="tap-target self-start text-meta text-ink-3 hover:text-ink-1"
                  data-testid="calendar-event-unlink"
                >
                  取消關聯
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-s-2">
                <button
                  type="button"
                  onClick={() => setLinkPickerOpen(true)}
                  className="flex items-center gap-s-2 rounded-md border border-hairline px-s-4 py-s-3 text-body-sm text-ink-1 hover:bg-black/5"
                  data-testid="calendar-event-link-open"
                >
                  <Link2 className="w-4 h-4 text-ink-3" aria-hidden />
                  關聯一個相簿
                </button>
                {linkError ? (
                  <p className="text-meta text-partner-strong" role="alert" data-testid="calendar-event-link-error">
                    {linkError}
                  </p>
                ) : null}
              </div>
            )}

            {event.source_id || event.recorded_by ? (
              <p className="text-meta text-ink-3" data-testid="calendar-event-audit">
                {event.recorded_by === myId ? "你" : "對方"}於{" "}
                {new Date(event.created_at).toLocaleDateString("zh-TW", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                記錄
              </p>
            ) : null}
          </SectionStack>
        ) : null}
      </PageContainer>

      <EventLinkPickerSheet
        open={linkPickerOpen}
        onOpenChange={setLinkPickerOpen}
        onPick={(type, id) => void handleLink(type, id)}
        busy={linkBusy}
      />

      {/*
        R1 (ADR-0080): the confirm dialog said 「刪除後就看不到這筆安排了，
        此操作無法復原」— but the delete is a soft delete with a 7-day window
        and a restore route, so that copy was the lie ADR-0078 called out. The
        bar is absolutely positioned inside the page container, so appearing
        moves nothing above it.
      */}
      {undo.item ? (
        <div
          data-testid="calendar-undo-slot"
          // **Fixed, and 108px up.** Two things the first screenshot pass got
          // wrong, both visible in `02-calendar-event-undo-floating.png`:
          //
          //  - `absolute` inside the page meant the bar scrolled away with the
          //    content and, anchored to a non-`relative` parent, resolved
          //    against the viewport anyway. `fixed` is what a global,
          //    attention-getting surface actually is (DESIGN §16 bans snackbar
          //    positioning, not this).
          //  - `bottom-24` (96px) put 「已刪除」 under the three-island pill: the
          //    islands are 60px at rest plus `bottom-4` plus the safe-area inset.
          //    108px clears all three. An undo bar that cannot be read is the
          //    one failure this pattern cannot have.
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-30 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message="事件已刪除"
              onUndo={() => restoreMutation.mutate()}
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

